import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { FiChevronDown, FiChevronUp } from 'react-icons/fi';
import Footer from '../components/Layout/Footer';

const FAQS = [
  {
    q: 'What is ReplyCraft?',
    a: 'ReplyCraft is AI-powered email reply generator that helps you craft the perfect response in seconds. It offers 5 reply tones, Fix My Draft mode, reply in 24+ languages, smart suggestion chips, context-aware categories, response length control, saved favorites, and custom signatures — all designed for daily professional use.'
  },
  {
    q: 'What are the two modes?',
    a: 'Generate Reply mode: paste an email you received and get AI-generated reply options. Fix My Draft mode: paste your own rough draft and ReplyCraft polishes it — fixing grammar, improving tone, and adding proper structure. Both modes support all tones, lengths, and languages.'
  },
  {
    q: 'What are Reply Instructions and Suggestion Chips?',
    a: 'Reply Instructions let you tell the AI exactly what to say — for example, "Thank them, mention I attached the signed form, and ask if they need anything else." Suggestion Chips are auto-detected actions based on the email content (like "Confirm availability", "Decline politely", "Attached docs") that you can click to populate your instructions.'
  },
  {
    q: 'Can I reply in a different language?',
    a: 'Yes! ReplyCraft supports 24+ languages including English, Spanish, French, German, Hindi, Portuguese, Chinese, Japanese, Korean, Arabic, Russian, Italian, and more. You can receive an email in one language and reply in another.'
  },
  {
    q: 'What are context categories?',
    a: 'Context categories help the AI tailor replies to your situation. Choose from: Job/Career, Client, Sales, Support, Internal, Personal, Complaint, or Networking. For example, a "Complaint" response leads with empathy, while a "Sales" reply includes a call-to-action.'
  },
  {
    q: 'How do credits work?',
    a: 'Credits are used each time you generate replies. 1 credit = 1 word generated. When you generate replies for an email, the total word count of all generated replies is deducted from your balance. For example, if 3 replies total 300 words, 300 credits are used.'
  },
  {
    q: 'How many free credits do I get?',
    a: 'Every new account gets 1,000 free credits — enough for about 2-3 reply generations to test the service.'
  },
  {
    q: 'What are the pricing plans?',
    a: 'We offer three credit packs: Starter (50,000 credits for $5), Pro (150,000 credits for $10 — best value), and Power (500,000 credits for $25). Credits never expire. No subscriptions.'
  },
  {
    q: 'Can I save my favorite replies?',
    a: 'Yes! Click the heart icon on any generated reply to save it. Access all your saved replies from the "Saved Replies" section in the sidebar. You can copy, email, or delete saved replies anytime.'
  },
  {
    q: 'How does the custom signature work?',
    a: 'Go to Account Settings and fill in your name, job title, company, and phone number. ReplyCraft will automatically sign all generated replies with your real information instead of generic placeholders.'
  },
  {
    q: 'What tones are available?',
    a: 'ReplyCraft offers 5 tones: Professional (formal, business-appropriate), Warm (friendly, empathetic), Assertive (confident, direct), Casual (informal, conversational), and Diplomatic (tactful, considerate). You can select one or all five.'
  },
  {
    q: 'Can I control reply length?',
    a: 'Yes. Choose Short (2-4 sentences, straight to the point), Medium (4-8 sentences, balanced), or Long (multiple paragraphs, detailed). This works in both Generate Reply and Fix My Draft modes.'
  },
  {
    q: 'Is my data private?',
    a: 'Yes. We do not store the content of your emails or messages after generating replies. Your data is processed in real-time and not used for AI training. See our Privacy Policy for details.'
  },
  {
    q: 'Are credits refundable?',
    a: 'No. All credit purchases are final and non-refundable. We recommend trying the free credits first to make sure ReplyCraft works for you before purchasing.'
  },
  {
    q: 'Can I use ReplyCraft for business?',
    a: 'Absolutely. ReplyCraft is built for daily professional use — salespeople, recruiters, customer support teams, freelancers, managers, and anyone who writes emails. The credit system scales to any team size.'
  },
  {
    q: 'How do I contact support?',
    a: 'Email us at support@replycraft.io or text (540) 322-7406. You can also use the Contact page to submit feedback or feature suggestions.'
  }
];

function FAQItem({ faq }) {
  const [open, setOpen] = useState(false);
  return (
    <div style={styles.faqItem}>
      <button onClick={() => setOpen(!open)} style={styles.faqQuestion}>
        <span>{faq.q}</span>
        {open ? <FiChevronUp size={18} /> : <FiChevronDown size={18} />}
      </button>
      {open && <p style={styles.faqAnswer}>{faq.a}</p>}
    </div>
  );
}

export default function FAQ() {
  return (
    <div style={styles.page}>
      <nav style={styles.nav}>
        <Link to="/" style={styles.brand}>ReplyCraft</Link>
      </nav>
      <div style={styles.container}>
        <h1 style={styles.title}>Frequently Asked Questions</h1>
        <p style={styles.subtitle}>Everything you need to know about ReplyCraft</p>
        <div style={styles.faqList}>
          {FAQS.map((faq, i) => <FAQItem key={i} faq={faq} />)}
        </div>
        <div style={styles.cta}>
          <p>Still have questions?</p>
          <Link to="/contact" style={styles.ctaBtn}>Contact Us</Link>
        </div>
      </div>
      <Footer />
    </div>
  );
}

const styles = {
  page: { minHeight: '100vh', background: 'var(--bg-primary)', display: 'flex', flexDirection: 'column' },
  nav: { padding: '16px 40px', borderBottom: '1px solid var(--border)' },
  brand: { fontSize: 20, fontWeight: 700, color: 'var(--accent)', textDecoration: 'none' },
  container: { maxWidth: 700, margin: '0 auto', padding: '40px 20px 60px', flex: 1 },
  title: { fontSize: 36, fontWeight: 800, textAlign: 'center', marginBottom: 8, color: 'var(--text-primary)' },
  subtitle: { textAlign: 'center', color: 'var(--text-secondary)', fontSize: 16, marginBottom: 40 },
  faqList: { display: 'flex', flexDirection: 'column', gap: 8 },
  faqItem: { background: 'var(--bg-secondary)', border: '1px solid var(--border)', borderRadius: 12, overflow: 'hidden' },
  faqQuestion: { width: '100%', display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '18px 20px', background: 'none', border: 'none', color: 'var(--text-primary)', fontSize: 15, fontWeight: 600, cursor: 'pointer', textAlign: 'left', gap: 12 },
  faqAnswer: { padding: '0 20px 18px', color: 'var(--text-secondary)', fontSize: 14, lineHeight: 1.7 },
  cta: { textAlign: 'center', marginTop: 48, color: 'var(--text-secondary)', fontSize: 16 },
  ctaBtn: { display: 'inline-block', marginTop: 12, padding: '12px 28px', background: 'var(--accent)', color: 'white', borderRadius: 10, fontSize: 15, fontWeight: 600, textDecoration: 'none' }
};
